import { useEffect, useState } from "react";
import { useUpdateTeamMember } from "../../hooks/useTeam";
import type { PreferredChannel, TeamMemberProfile } from "../../lib/team";
import { Field } from "./TeamDialogs";

export function MemberProfileCard({ member }: { member: TeamMemberProfile }) {
  const update = useUpdateTeamMember();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => memberForm(member));
  useEffect(() => setForm(memberForm(member)), [member]);

  const save = () => update.mutate({
    id: member.id,
    patch: { ...form, hourly_rate: Number(form.hourly_rate.replace(",", ".")) || 0 },
  }, { onSuccess: () => setEditing(false) });

  const [deaktivierenArmed, setDeaktivierenArmed] = useState(false);
  const [statusError, setStatusError] = useState("");
  const toggleActive = () => {
    if (member.active && !deaktivierenArmed) {
      setDeaktivierenArmed(true);
      return;
    }
    setDeaktivierenArmed(false);
    setStatusError("");
    // Ohne onError bleibt ein fehlgeschlagener Wechsel stumm — der Knopf sieht
    // dann aus, als haette er nichts getan.
    update.mutate({ id: member.id, patch: { active: !member.active } }, {
      onError: () => setStatusError(member.active
        ? "Deaktivieren hat nicht geklappt. Bitte erneut versuchen."
        : "Aktivieren hat nicht geklappt. Bitte erneut versuchen."),
    });
  };

  return (
    <section className="team-card team-profile-card">
      <header className="team-card-head"><div><span className="team-section-label">Profil</span><h2>Stammdaten</h2></div><button type="button" className="team-button secondary compact" onClick={() => setEditing((value) => !value)}>{editing ? "Abbrechen" : "Bearbeiten"}</button></header>
      {editing ? (
        <>
          <div className="team-form-grid">
            <Field label="Name"><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
            <Field label="Rolle"><select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}><option value="helfer">Helfer</option><option value="vorarbeiter">Vorarbeiter</option><option value="azubi">Azubi</option><option value="aushilfe">Aushilfe</option></select></Field>
            <Field label="Stundenlohn (€)"><input inputMode="decimal" value={form.hourly_rate} onChange={(event) => setForm({ ...form, hourly_rate: event.target.value })} /></Field>
            <Field label="Telefon"><input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></Field>
            <Field label="E-Mail"><input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></Field>
            <Field label="Bevorzugter Kanal"><select value={form.preferred_channel} onChange={(event) => setForm({ ...form, preferred_channel: event.target.value as PreferredChannel })}><option value="email">E-Mail</option><option value="telegram">Telegram</option><option value="whatsapp">WhatsApp</option></select></Field>
            <Field label="Kontoinhaber"><input value={form.kontoinhaber} onChange={(event) => setForm({ ...form, kontoinhaber: event.target.value })} /></Field>
            <Field label="IBAN" full><input autoComplete="off" value={form.iban} onChange={(event) => setForm({ ...form, iban: event.target.value.toLocaleUpperCase("de") })} /></Field>
            <Field label="BIC"><input value={form.bic} onChange={(event) => setForm({ ...form, bic: event.target.value.toLocaleUpperCase("de") })} /></Field>
          </div>
          {update.isError && <p className="team-error">Prüfe die Angaben. Die Daten konnten nicht gespeichert werden.</p>}
          <div className="team-inline-actions"><button type="button" className="team-button primary" disabled={!form.name.trim() || update.isPending} onClick={save}>{update.isPending ? "Speichert …" : "Änderungen speichern"}</button></div>
        </>
      ) : (
        <dl className="team-details">
          <Detail label="Telefon" value={member.phone} href={member.phone ? `tel:${member.phone}` : undefined} />
          <Detail label="E-Mail" value={member.email} href={member.email ? `mailto:${member.email}` : undefined} />
          <Detail label="Kontaktkanal" value={channelLabel(member.preferred_channel)} />
          <Detail label="Stundenlohn" value={`${member.hourly_rate.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`} />
          <Detail label="Kontoinhaber" value={member.kontoinhaber} />
          <Detail label="IBAN" value={member.iban} mono />
          <Detail label="BIC" value={member.bic} mono />
        </dl>
      )}
      <div className="team-profile-status"><div><strong>{member.active ? "Aktiv im Team" : "Nicht mehr aktiv"}</strong><span>{member.active ? "Kann für neue Einsätze eingeplant werden." : "Bleibt in historischen Einsätzen erhalten."}</span></div><button type="button" className={`team-button ${member.active ? "danger" : "secondary"}`} disabled={update.isPending} onClick={toggleActive}>{member.active ? (deaktivierenArmed ? "Wirklich deaktivieren" : "Deaktivieren") : "Wieder aktivieren"}</button></div>
      {statusError && <p className="team-error">{statusError}</p>}
    </section>
  );
}

function memberForm(member: TeamMemberProfile) {
  return {
    name: member.name,
    role: member.role || "helfer",
    phone: member.phone,
    email: member.email,
    hourly_rate: String(member.hourly_rate),
    preferred_channel: member.preferred_channel,
    kontoinhaber: member.kontoinhaber,
    iban: member.iban,
    bic: member.bic,
  };
}

function channelLabel(channel: PreferredChannel) {
  return channel === "telegram" ? "Telegram" : channel === "whatsapp" ? "WhatsApp" : "E-Mail";
}

function Detail({ label, value, href, mono }: { label: string; value?: string; href?: string; mono?: boolean }) {
  return <div><dt>{label}</dt><dd className={mono ? "mono" : ""}>{href ? <a href={href}>{value}</a> : value || "—"}</dd></div>;
}
